/**
 * Lectura de archivos KML y KMZ.
 *
 * Sin dependencias: el KML se recorre con expresiones sobre el texto y el KMZ
 * se descomprime con `DecompressionStream`, que ya viene en el navegador. Meter
 * una librería de ZIP solo para esto agregaría peso a una app que tiene que
 * caber en un teléfono y funcionar sin conexión.
 *
 * Qué se importa y qué no:
 *
 *  - `Point` → un punto. Es el caso normal: una zona de aterrizaje, un helipuerto
 *    marcado a mano, un punto de referencia visual.
 *  - `LineString` / `LinearRing` → la secuencia completa de vértices, numerada.
 *    Sirve para traer una ruta ya trazada en Google Earth.
 *  - `Polygon` → se leen sus vértices igual que una línea, pero se avisa: un
 *    polígono suele ser un área (una concesión, una zona de trabajo), no una
 *    lista de puntos de navegación.
 *
 * La altitud del KML se ignora a propósito para la elevación del terreno. En
 * Google Earth casi siempre es 0 o `clampToGround`, y un cero se ve igual que
 * una cota real medida. La elevación se consulta después contra el modelo
 * digital, que es la fuente que la app ya usa para el perfil.
 */

/** Quita el prefijo de espacio de nombres: `kml:Placemark` → `Placemark`. */
const SIN_PREFIJO = "(?:[A-Za-z0-9_.-]+:)?";

/** Devuelve el contenido de la primera etiqueta `nombre` dentro de `bloque`. */
function contenidoDe(bloque, nombre) {
  const re = new RegExp(`<${SIN_PREFIJO}${nombre}(?:\\s[^>]*)?>([\\s\\S]*?)</${SIN_PREFIJO}${nombre}>`, "i");
  const m = bloque.match(re);
  return m ? m[1] : null;
}

/** Limpia CDATA, entidades XML y espacios. */
function texto(valor) {
  if (valor === null || valor === undefined) return "";
  return String(valor)
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, "$1")
    .replace(/<[^>]*>/g, "")
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Convierte el bloque `<coordinates>` de KML en pares lat/lon.
 *
 * El orden en KML es lon,lat,alt — al revés de como se escribe una posición en
 * aviación. Invertirlo mal produce puntos en el hemisferio equivocado, así que
 * además se validan los rangos: una latitud fuera de ±90 es señal de que el
 * archivo trae otro orden o está corrupto, y ese punto se descarta con aviso.
 */
function leerCoordenadas(bloque) {
  const crudo = contenidoDe(bloque, "coordinates");
  if (!crudo) return { pares: [], descartados: 0 };
  const pares = [];
  let descartados = 0;
  for (const tupla of crudo.trim().split(/\s+/)) {
    if (!tupla) continue;
    const partes = tupla.split(",");
    if (partes.length < 2) { descartados += 1; continue; }
    const lon = Number(partes[0]);
    const lat = Number(partes[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
      descartados += 1;
      continue;
    }
    pares.push({ lat, lon });
  }
  return { pares, descartados };
}

/**
 * Lee un KML y devuelve los puntos que contiene.
 *
 * @returns {{puntos: Array<{nombre: string, lat: number, lon: number}>, avisos: string[]}}
 */
export function parsearKml(contenido) {
  const avisos = [];
  const puntos = [];

  if (typeof contenido !== "string" || !contenido.trim()) {
    return { puntos, avisos: ["El archivo está vacío."] };
  }
  if (!/<\s*(?:[A-Za-z0-9_.-]+:)?kml[\s>]/i.test(contenido) && !/<\s*(?:[A-Za-z0-9_.-]+:)?Placemark[\s>]/i.test(contenido)) {
    return { puntos, avisos: ["No parece un archivo KML: no se encontró ninguna marca de ubicación."] };
  }

  const bloques = contenido.match(new RegExp(`<${SIN_PREFIJO}Placemark(?:\\s[^>]*)?>[\\s\\S]*?</${SIN_PREFIJO}Placemark>`, "gi")) || [];
  if (bloques.length === 0) {
    return { puntos, avisos: ["El archivo no contiene marcas de ubicación."] };
  }

  let sinNombre = 0;
  let descartadosTotal = 0;
  let poligonos = 0;

  bloques.forEach((bloque, indice) => {
    let nombre = texto(contenidoDe(bloque, "name"));
    if (!nombre) {
      sinNombre += 1;
      nombre = `PUNTO ${indice + 1}`;
    }

    const esPunto = new RegExp(`<${SIN_PREFIJO}Point[\\s>]`, "i").test(bloque);
    const esLinea = new RegExp(`<${SIN_PREFIJO}(?:LineString|LinearRing)[\\s>]`, "i").test(bloque);
    const esPoligono = new RegExp(`<${SIN_PREFIJO}Polygon[\\s>]`, "i").test(bloque);
    if (esPoligono) poligonos += 1;

    const { pares, descartados } = leerCoordenadas(bloque);
    descartadosTotal += descartados;
    if (pares.length === 0) return;

    if (esPunto && !esLinea && !esPoligono) {
      puntos.push({ nombre: nombre.toUpperCase(), lat: pares[0].lat, lon: pares[0].lon });
      return;
    }

    // Línea o polígono: cada vértice pasa a ser un punto numerado.
    pares.forEach((p, i) => {
      puntos.push({
        nombre: `${nombre.toUpperCase()} ${String(i + 1).padStart(2, "0")}`,
        lat: p.lat,
        lon: p.lon,
      });
    });
  });

  if (sinNombre > 0) avisos.push(`${sinNombre} marca(s) sin nombre: se numeraron automáticamente.`);
  if (descartadosTotal > 0) avisos.push(`${descartadosTotal} coordenada(s) ilegibles o fuera de rango: se descartaron.`);
  if (poligonos > 0) avisos.push(`${poligonos} polígono(s) importados como vértices sueltos. Un polígono suele delimitar un área, no una ruta: revisa si son puntos de navegación.`);
  if (puntos.length === 0) avisos.push("No se pudo leer ninguna coordenada válida.");

  return { puntos, avisos };
}

/* ────────────────────────── KMZ (ZIP con un KML dentro) ─────────────────── */

const FIRMA_FIN_DIRECTORIO = 0x06054b50;
const FIRMA_ENTRADA = 0x02014b50;

/**
 * Extrae el KML de un KMZ.
 *
 * Un KMZ es un ZIP. Se lee el directorio central desde el final del archivo,
 * se busca la primera entrada `.kml` y se descomprime. Solo se admiten los dos
 * métodos que usa Google Earth: almacenado (0) y deflate (8).
 */
export async function extraerKmlDeKmz(buffer) {
  const datos = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const vista = new DataView(datos.buffer, datos.byteOffset, datos.byteLength);

  // El fin del directorio central está al final, después de un comentario de
  // longitud variable. Se busca la firma hacia atrás.
  let finDir = -1;
  for (let i = datos.length - 22; i >= 0 && i >= datos.length - 22 - 65535; i -= 1) {
    if (vista.getUint32(i, true) === FIRMA_FIN_DIRECTORIO) { finDir = i; break; }
  }
  if (finDir < 0) throw new Error("El archivo no es un KMZ válido: no se encontró el índice del comprimido.");

  const cantidad = vista.getUint16(finDir + 10, true);
  let puntero = vista.getUint32(finDir + 16, true);

  for (let n = 0; n < cantidad; n += 1) {
    if (vista.getUint32(puntero, true) !== FIRMA_ENTRADA) break;
    const metodo = vista.getUint16(puntero + 10, true);
    const tamComprimido = vista.getUint32(puntero + 20, true);
    const largoNombre = vista.getUint16(puntero + 28, true);
    const largoExtra = vista.getUint16(puntero + 30, true);
    const largoComentario = vista.getUint16(puntero + 32, true);
    const offsetLocal = vista.getUint32(puntero + 42, true);
    const nombre = new TextDecoder().decode(datos.subarray(puntero + 46, puntero + 46 + largoNombre));

    if (nombre.toLowerCase().endsWith(".kml")) {
      // La cabecera local repite el nombre y los extras, con largos propios.
      const largoNombreLocal = vista.getUint16(offsetLocal + 26, true);
      const largoExtraLocal = vista.getUint16(offsetLocal + 28, true);
      const inicio = offsetLocal + 30 + largoNombreLocal + largoExtraLocal;
      const comprimido = datos.subarray(inicio, inicio + tamComprimido);

      if (metodo === 0) return new TextDecoder().decode(comprimido);
      if (metodo !== 8) throw new Error(`El KMZ usa un método de compresión no admitido (${metodo}).`);
      if (typeof DecompressionStream === "undefined") {
        throw new Error("Este navegador no puede abrir KMZ. Descomprime el archivo y carga el .kml que tiene adentro.");
      }
      const flujo = new Blob([comprimido]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
      return new TextDecoder().decode(await new Response(flujo).arrayBuffer());
    }

    puntero += 46 + largoNombre + largoExtra + largoComentario;
  }

  throw new Error("El KMZ no contiene ningún archivo .kml adentro.");
}

/**
 * Lee un archivo elegido por el usuario y devuelve sus puntos.
 *
 * Acepta `.kml` y `.kmz`. No toca la base: devuelve lo leído para que quien
 * llama decida qué guardar. Importar a ciegas un archivo de 800 vértices
 * llenaría la base sin que nadie lo haya visto.
 */
export async function leerArchivoDePuntos(archivo) {
  const nombre = archivo?.name || "archivo";
  const esKmz = /\.kmz$/i.test(nombre);
  const esKml = /\.kml$/i.test(nombre);
  if (!esKmz && !esKml) {
    return { puntos: [], avisos: ["Solo se admiten archivos .kml y .kmz."], archivo: nombre };
  }

  try {
    const contenido = esKmz
      ? await extraerKmlDeKmz(await archivo.arrayBuffer())
      : await archivo.text();
    const { puntos, avisos } = parsearKml(contenido);
    return { puntos, avisos, archivo: nombre };
  } catch (e) {
    return { puntos: [], avisos: [e.message || "No se pudo leer el archivo."], archivo: nombre };
  }
}
